import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createEditor,
  EditorApi,
  EditorMapState,
  EditorUnitEntry,
  TileInfo,
} from 'phaser-wars';
import { get, post, put } from '../../api';
import { generateMapCode } from '../../util/export-map-code';

interface PlayerSlot {
  startingFunds: number;
}

interface CustomMapRecord {
  id: string;
  name: string;
  description: string;
  width: number;
  height: number;
  terrainData: { [key: string]: string };
  ownerOverrides: { [key: string]: number } | null;
  definitionOverrides: { [key: string]: string } | null;
  playerSlots: PlayerSlot[];
  initialUnits?: { [key: string]: EditorUnitEntry } | null;
}

const TERRAIN_SWATCHES: { type: string; label: string; color: string }[] = [
  { type: 'grass', label: 'Grass', color: '#7ec850' },
  { type: 'forest', label: 'Forest', color: '#2f6b2f' },
  { type: 'mountain', label: 'Mountain', color: '#8a8a8a' },
  { type: 'hill', label: 'Hill', color: '#c2a878' },
  { type: 'road', label: 'Road', color: '#8a6d3b' },
  { type: 'river', label: 'River', color: '#4a90d9' },
  { type: 'sea', label: 'Sea', color: '#1b4a6b' },
  { type: 'beach', label: 'Beach', color: '#e8d4a0' },
  { type: 'wall', label: 'Wall', color: '#b3a884' },
  { type: 'gate', label: 'Gate', color: '#8a7a4a' },
];

const PROPERTY_SWATCHES: { type: string; label: string; color: string }[] = [
  { type: 'fort', label: 'Fort', color: '#8b3a3a' },
  { type: 'barracks', label: 'Barracks', color: '#b5651d' },
  { type: 'siegeHut', label: 'Siege Hut', color: '#6d6d99' },
  { type: 'village', label: 'Village', color: '#c97b3d' },
  { type: 'farm', label: 'Farm', color: '#d4af37' },
  { type: 'workshop', label: 'Workshop', color: '#a5851a' },
  { type: 'windmill', label: 'Windmill', color: '#e8d44d' },
  { type: 'mine', label: 'Mine', color: '#3c3833' },
  { type: 'dock', label: 'Dock', color: '#6b4a3a' },
];

const UNIT_TYPES: { type: string; label: string }[] = [
  { type: 'spearman', label: 'Spearman' },
  { type: 'archer', label: 'Archer' },
  { type: 'knight', label: 'Knight' },
  { type: 'lancer', label: 'Lancer' },
  { type: 'catapult', label: 'Catapult' },
  { type: 'ram', label: 'Ram' },
  { type: 'worker', label: 'Worker' },
  { type: 'ship', label: 'Ship' },
];

const PLAYER_COLORS = ['#68b8e8', '#d43318', '#f7b728', '#5db854'];
const NEUTRAL_COLOR = '#9e9e9e';

const OVERRIDE_OPTIONS: { id: string | null; label: string }[] = [
  { id: null, label: 'Default' },
];

const DEFAULT_WIDTH = 10;
const DEFAULT_HEIGHT = 10;

export default function EditorMapPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [playerSlots, setPlayerSlots] = useState<PlayerSlot[]>([
    { startingFunds: 100 },
    { startingFunds: 100 },
  ]);
  const [initialState, setInitialState] = useState<
    EditorMapState | undefined
  >();
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [api, setApi] = useState<EditorApi | null>(null);
  const [mapState, setMapState] = useState<EditorMapState | null>(null);
  const [activeToolKind, setActiveToolKind] = useState<
    'terrain' | 'owner' | 'override' | 'unit' | 'removeUnit'
  >('terrain');
  const [selectedTerrain, setSelectedTerrain] = useState('grass');
  const [selectedPlayerId, setSelectedPlayerId] = useState(0);
  const [selectedOverride, setSelectedOverride] = useState<string | null>(null);
  const [selectedUnitType, setSelectedUnitType] = useState('spearman');
  const [panMode, setPanMode] = useState(true);
  const [tileInfo, setTileInfo] = useState<TileInfo | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const [widthInput, setWidthInput] = useState(DEFAULT_WIDTH);
  const [heightInput, setHeightInput] = useState(DEFAULT_HEIGHT);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [exportText, setExportText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  // A brand-new, never-saved map always counts as dirty — there's no id yet to test with.
  const [dirty, setDirty] = useState(isNew);
  const [savedMapId, setSavedMapId] = useState<string | null>(
    isNew ? null : id ?? null
  );
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // Fetch the existing map before mounting Phaser — the editor's initial
  // terrain/overrides are set once, at scene creation time, so they have to
  // be known before createEditor is ever called.
  useEffect(() => {
    if (isNew) {
      return;
    }
    setLoading(true);
    get(`custom-maps/${id}`)
      .then((map: CustomMapRecord) => {
        setName(map.name);
        setDescription(map.description);
        setPlayerSlots(map.playerSlots);
        setWidthInput(map.width);
        setHeightInput(map.height);
        setInitialState({
          width: map.width,
          height: map.height,
          terrainData: map.terrainData,
          ownerOverrides: map.ownerOverrides ?? {},
          definitionOverrides: map.definitionOverrides ?? {},
          units: map.initialUnits ?? {},
        });
      })
      .catch(() => setLoadError('Could not load this map.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (loading || loadError || !container.current) {
      return;
    }
    const { width = 0, height = 0 } = container.current.getBoundingClientRect();
    while (container.current.firstChild) {
      container.current.removeChild(container.current.firstChild);
    }
    const game = createEditor({
      config: { width, height, parent: 'editor-container' },
      data: { initialState },
      onReady: (editorApi) => {
        setApi(editorApi);
        setMapState(editorApi.getState());
        editorApi.onChange((state) => {
          setMapState(state);
          setDirty(true);
        });
        editorApi.onTileHover(setTileInfo);
        editorApi.onHistoryChange((undoAvailable, redoAvailable) => {
          setCanUndo(undoAvailable);
          setCanRedo(redoAvailable);
        });
      },
    });

    return () => {
      game.destroy(true);
      setApi(null);
    };
    // Only remount Phaser when loading finishes — initialState is read once
    // at that point (see the effect above), not on every subsequent edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadError]);

  const usedPlayerIds = useMemo(() => {
    const overrides = mapState?.ownerOverrides ?? {};
    return new Set(
      Object.values(overrides).filter((playerId) => playerId >= 0)
    );
  }, [mapState]);

  const setTerrainTool = (terrainType: string) => {
    setActiveToolKind('terrain');
    setSelectedTerrain(terrainType);
    api?.setTool({ kind: 'terrain', terrainType });
  };

  const setOwnerTool = (playerId: number) => {
    setActiveToolKind('owner');
    setSelectedPlayerId(playerId);
    api?.setTool({ kind: 'owner', playerId });
  };

  const setOverrideTool = (definitionId: string | null) => {
    setActiveToolKind('override');
    setSelectedOverride(definitionId);
    api?.setTool({ kind: 'override', definitionId });
  };

  const setUnitTool = (unitType: string, playerId: number) => {
    setActiveToolKind('unit');
    setSelectedUnitType(unitType);
    api?.setTool({ kind: 'unit', unitType, playerId });
  };

  const setRemoveUnitTool = () => {
    setActiveToolKind('removeUnit');
    api?.setTool({ kind: 'removeUnit' });
  };

  const setOwner = (playerId: number) => {
    setSelectedPlayerId(playerId);
    setOwnerTool(playerId);
  };

  const handlePanToggle = (mode: boolean) => {
    setPanMode(mode);
    api?.setPanMode(mode);
  };

  const handleResize = () => {
    api?.resize(widthInput, heightInput);
  };

  const addPlayerSlot = () => {
    setDirty(true);
    setPlayerSlots((slots) =>
      slots.length >= 4 ? slots : [...slots, { startingFunds: 100 }]
    );
  };
  const removePlayerSlot = (index: number) => {
    setDirty(true);
    setPlayerSlots((slots) => slots.filter((_, i) => i !== index));
  };
  const updateStartingFunds = (index: number, value: number) => {
    setDirty(true);
    setPlayerSlots((slots) =>
      slots.map((slot, i) =>
        i === index ? { ...slot, startingFunds: value } : slot
      )
    );
  };

  const canRemoveSlot = (index: number) =>
    playerSlots.length > 1 && !usedPlayerIds.has(index);

  const maxUsedPlayerId =
    usedPlayerIds.size > 0 ? Math.max(...Array.from(usedPlayerIds)) : -1;
  const invalidPlayerCount = maxUsedPlayerId >= playerSlots.length;

  const saveMap = async (): Promise<string | null> => {
    if (!mapState) {
      return null;
    }
    setSaving(true);
    setSaveError(null);
    const payload = {
      name,
      description,
      width: mapState.width,
      height: mapState.height,
      terrainData: mapState.terrainData,
      ownerOverrides: mapState.ownerOverrides,
      definitionOverrides: mapState.definitionOverrides,
      initialUnits: mapState.units,
      playerSlots,
    };
    try {
      let savedId = id ?? null;
      if (isNew) {
        const created = await post<{ id: string }>('custom-maps', payload);
        savedId = created.id;
      } else {
        await put(`custom-maps/${id}`, payload);
      }
      setSavedMapId(savedId);
      setDirty(false);
      return savedId;
    } catch (e) {
      setSaveError('Could not save this map. Please try again.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const savedId = await saveMap();
    if (savedId) {
      navigate('/editor');
    }
  };

  const handleTestMap = () => {
    if (dirty) {
      setShowTestModal(true);
    } else if (savedMapId) {
      navigate(`/setup?mapId=${savedMapId}`);
    }
  };

  const handleSaveAndPlay = async () => {
    const savedId = await saveMap();
    setShowTestModal(false);
    if (savedId) {
      navigate(`/setup?mapId=${savedId}`);
    }
  };

  const handleExport = () => {
    if (!mapState) {
      return;
    }
    setExportText(
      generateMapCode({
        name: name || 'Custom Map',
        description,
        width: mapState.width,
        height: mapState.height,
        terrainData: mapState.terrainData,
        ownerOverrides: mapState.ownerOverrides,
        definitionOverrides: mapState.definitionOverrides,
        initialUnits: mapState.units,
        playerSlots,
      })
    );
    setCopied(false);
    setShowExportModal(true);
  };

  const handleExitSave = () => {
    setShowExitModal(false);
    handleSave();
  };

  const handleExitWithoutSaving = () => {
    navigate('/editor');
  };

  const handleCopy = async () => {
    if (!exportText) {
      return;
    }
    await navigator.clipboard.writeText(exportText);
    setCopied(true);
  };

  return (
    <div>
      {loading && (
        <p
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          Loading…
        </p>
      )}
      {loadError && (
        <p
          className="has-text-danger"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {loadError}
        </p>
      )}
      {!loading && !loadError && (
        <>
          <div
            id="editor-container"
            ref={container}
            className="fullscreen-canvas"
          />

          <div
            style={{
              position: 'fixed',
              top: 'calc(10px + env(safe-area-inset-top))',
              right: 'calc(10px + env(safe-area-inset-right))',
              zIndex: 10,
              display: 'flex',
              gap: 8,
            }}
          >
            <button
              className={`button is-small ${!panMode ? 'is-link' : 'is-light'}`}
              onClick={() => handlePanToggle(!panMode)}
            >
              Lock Camera
            </button>
            <button
              className="button is-small is-light"
              onClick={() => setShowExitModal(true)}
            >
              Exit
            </button>
          </div>

          <div
            style={{
              position: 'fixed',
              bottom: 'calc(10px + env(safe-area-inset-bottom))',
              right: 'calc(10px + env(safe-area-inset-right))',
              zIndex: 10,
              display: 'flex',
              gap: 8,
            }}
          >
            <button
              className="button is-small is-light"
              onClick={() => api?.undo()}
              disabled={!canUndo}
              title="Undo"
            >
              Undo
            </button>
            <button
              className="button is-small is-light"
              onClick={() => api?.redo()}
              disabled={!canRedo}
              title="Redo"
            >
              Redo
            </button>
          </div>

          <div
            style={{
              position: 'fixed',
              bottom: 'calc(10px + env(safe-area-inset-bottom))',
              left: 'calc(10px + env(safe-area-inset-left))',
              zIndex: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.75rem',
              lineHeight: '1.4',
              pointerEvents: 'none',
              fontFamily: 'monospace',
            }}
          >
            {tileInfo ? (
              <>
                ({tileInfo.x}, {tileInfo.y}){' '}
                {tileInfo.terrainType.charAt(0).toUpperCase() +
                  tileInfo.terrainType.slice(1)}
                {tileInfo.unit && (
                  <>
                    {' '}
                    <span
                      style={{
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        verticalAlign: 'middle',
                        backgroundColor:
                          tileInfo.unit.playerId >= 0
                            ? PLAYER_COLORS[
                                Math.min(
                                  tileInfo.unit.playerId,
                                  PLAYER_COLORS.length - 1
                                )
                              ]
                            : NEUTRAL_COLOR,
                      }}
                    />
                    {tileInfo.unit.type.charAt(0).toUpperCase() +
                      tileInfo.unit.type.slice(1)}
                  </>
                )}
                {tileInfo.propertyDefinition && (
                  <>
                    {' '}
                    <span
                      style={{
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        verticalAlign: 'middle',
                        backgroundColor:
                          tileInfo.ownerId !== undefined &&
                          tileInfo.ownerId >= 0
                            ? PLAYER_COLORS[
                                Math.min(
                                  tileInfo.ownerId,
                                  PLAYER_COLORS.length - 1
                                )
                              ]
                            : NEUTRAL_COLOR,
                      }}
                    />
                    {tileInfo.propertyDefinition.charAt(0).toUpperCase() +
                      tileInfo.propertyDefinition.slice(1)}
                  </>
                )}
              </>
            ) : null}
          </div>

          <div
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              left: 0,
              width: '320px',
              maxWidth: 'calc(100vw - 48px)',
              zIndex: 20,
              transform: panelCollapsed ? 'translateX(-100%)' : 'translateX(0)',
              transition: 'transform 0.25s ease',
              overflowY: 'auto',
              backgroundColor: 'var(--panel-bg)',
              boxShadow: panelCollapsed ? 'none' : '4px 0 32px rgba(0,0,0,0.5)',
              borderRight: '1px solid rgba(68,68,102,0.6)',
            }}
          >
            <div
              className="box"
              style={{
                margin: 0,
                borderRadius: 0,
                minHeight: '100%',
                boxShadow: 'none',
                backgroundColor: 'var(--panel-bg-raised)',
                paddingTop: 'calc(1.25rem + env(safe-area-inset-top))',
                paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))',
                paddingLeft: 'calc(1.25rem + env(safe-area-inset-left))',
              }}
            >
              <div className="field">
                <button
                  className="button is-primary is-fullwidth"
                  onClick={handleSave}
                  disabled={saving || invalidPlayerCount || !mapState}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>

              <div className="field">
                <button
                  className="button is-light is-fullwidth"
                  onClick={handleTestMap}
                  disabled={saving || invalidPlayerCount || !mapState}
                >
                  Test Map
                </button>
              </div>

              <div className="field">
                <button
                  className="button is-light is-fullwidth"
                  onClick={handleExport}
                  disabled={!mapState}
                >
                  Export as Code
                </button>
              </div>

              <hr />

              <div className="field">
                <button
                  className="button is-light is-fullwidth"
                  onClick={() => setShowConfigModal(true)}
                >
                  Configuration
                </button>
              </div>

              <div className="field">
                <label className="label">Terrain</label>
                <div className="buttons" style={{ flexWrap: 'wrap' }}>
                  {TERRAIN_SWATCHES.map((swatch) => (
                    <button
                      key={swatch.type}
                      className="button is-small"
                      style={{
                        borderWidth: '2px',
                        borderColor:
                          activeToolKind === 'terrain' &&
                          selectedTerrain === swatch.type
                            ? '#3273dc'
                            : 'transparent',
                      }}
                      onClick={() => setTerrainTool(swatch.type)}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          backgroundColor: swatch.color,
                          marginRight: '6px',
                        }}
                      />
                      {swatch.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="label">Properties</label>
                <div className="buttons" style={{ flexWrap: 'wrap' }}>
                  {PROPERTY_SWATCHES.map((swatch) => (
                    <button
                      key={swatch.type}
                      className="button is-small"
                      style={{
                        borderWidth: '2px',
                        borderColor:
                          activeToolKind === 'terrain' &&
                          selectedTerrain === swatch.type
                            ? '#3273dc'
                            : 'transparent',
                      }}
                      onClick={() => setTerrainTool(swatch.type)}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          backgroundColor: swatch.color,
                          marginRight: '6px',
                        }}
                      />
                      {swatch.label}
                    </button>
                  ))}
                  <button
                    className={`button is-small ${
                      activeToolKind === 'unit' &&
                      selectedUnitType === 'watchTower'
                        ? 'is-link'
                        : ''
                    }`}
                    onClick={() => setUnitTool('watchTower', selectedPlayerId)}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor:
                          selectedPlayerId >= 0
                            ? PLAYER_COLORS[
                                Math.min(
                                  selectedPlayerId,
                                  PLAYER_COLORS.length - 1
                                )
                              ]
                            : NEUTRAL_COLOR,
                        marginRight: '6px',
                      }}
                    />
                    WatchTower
                  </button>
                </div>
              </div>

              <div className="field">
                <label className="label">Units</label>
                <div className="buttons" style={{ flexWrap: 'wrap' }}>
                  {UNIT_TYPES.map((unit) => (
                    <button
                      key={unit.type}
                      className={`button is-small ${
                        activeToolKind === 'unit' &&
                        selectedUnitType === unit.type
                          ? 'is-link'
                          : ''
                      }`}
                      onClick={() => setUnitTool(unit.type, selectedPlayerId)}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          backgroundColor:
                            selectedPlayerId >= 0
                              ? PLAYER_COLORS[
                                  Math.min(
                                    selectedPlayerId,
                                    PLAYER_COLORS.length - 1
                                  )
                                ]
                              : NEUTRAL_COLOR,
                          marginRight: '6px',
                        }}
                      />
                      {unit.label}
                    </button>
                  ))}
                  <button
                    className={`button is-small ${
                      activeToolKind === 'removeUnit' ? 'is-danger' : ''
                    }`}
                    onClick={setRemoveUnitTool}
                  >
                    Remove Unit
                  </button>
                </div>
              </div>

              <div className="field">
                <label className="label">
                  Assign Owner (unit first, then property)
                </label>
                <div className="buttons" style={{ flexWrap: 'wrap' }}>
                  {playerSlots.map((_, index) => (
                    <button
                      key={index}
                      className={`button is-small ${
                        activeToolKind === 'owner' && selectedPlayerId === index
                          ? 'is-link'
                          : ''
                      }`}
                      onClick={() => setOwner(index)}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          backgroundColor:
                            index < PLAYER_COLORS.length
                              ? PLAYER_COLORS[index]
                              : NEUTRAL_COLOR,
                          marginRight: '6px',
                        }}
                      />
                      Player {index + 1}
                    </button>
                  ))}
                  <button
                    className={`button is-small ${
                      activeToolKind === 'owner' && selectedPlayerId === -1
                        ? 'is-link'
                        : ''
                    }`}
                    onClick={() => setOwner(-1)}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: NEUTRAL_COLOR,
                        marginRight: '6px',
                      }}
                    />
                    Neutral
                  </button>
                </div>
              </div>

              <div className="field">
                <label className="label">
                  Property Override (click a property)
                </label>
                <div className="buttons" style={{ flexWrap: 'wrap' }}>
                  {OVERRIDE_OPTIONS.map((option) => (
                    <button
                      key={option.label}
                      className={`button is-small ${
                        activeToolKind === 'override' &&
                        selectedOverride === option.id
                          ? 'is-link'
                          : ''
                      }`}
                      onClick={() => setOverrideTool(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {saveError && <p className="has-text-danger">{saveError}</p>}
            </div>
          </div>

          <button
            onClick={() => setPanelCollapsed(!panelCollapsed)}
            className="button is-small"
            style={{
              position: 'fixed',
              top: '50%',
              left: 'env(safe-area-inset-left)',
              zIndex: 30,
              transform: `translateY(-50%) translateX(${
                panelCollapsed ? 0 : 320
              }px)`,
              transition: 'transform 0.25s ease',
              borderRadius: '0 6px 6px 0',
              border: '1px solid var(--panel-border)',
              borderLeft: 'none',
              padding: '12px 7px',
              backgroundColor: 'var(--panel-bg)',
              boxShadow: '2px 0 16px rgba(0,0,0,0.4)',
              color: 'var(--text-secondary)',
              minWidth: 'auto',
              height: 'auto',
              fontSize: '0.85rem',
              lineHeight: 1,
            }}
            title={panelCollapsed ? 'Show panel' : 'Hide panel'}
          >
            {panelCollapsed ? '▶︎' : '◀︎'}
          </button>
        </>
      )}

      <div className={`modal ${showExitModal ? 'is-active' : ''}`}>
        <div
          className="modal-background"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowExitModal(false)}
        />
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--panel-bg)',
            color: 'var(--text-primary)',
            border: '1px solid rgba(68,68,102,0.6)',
            borderRadius: 10,
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            margin: '0 16px',
          }}
        >
          <div
            style={{
              padding: '20px 24px 12px',
              borderBottom: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2
              className="title is-5 mb-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Exit Editor
            </h2>
            <button
              className="delete is-medium"
              aria-label="close"
              onClick={() => setShowExitModal(false)}
            />
          </div>
          <div style={{ padding: '20px 24px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              Do you want to save before you leave?
            </p>
          </div>
          <div
            style={{
              padding: '12px 24px 20px',
              borderTop: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <button
              className="button is-light"
              onClick={() => setShowExitModal(false)}
            >
              Cancel
            </button>
            <button
              className="button is-light"
              onClick={handleExitWithoutSaving}
            >
              Exit without Saving
            </button>
            <button
              className="button is-primary"
              onClick={handleExitSave}
              disabled={saving || invalidPlayerCount || !mapState}
            >
              {saving ? 'Saving…' : 'Save & Exit'}
            </button>
          </div>
        </div>
      </div>

      <div className={`modal ${showTestModal ? 'is-active' : ''}`}>
        <div
          className="modal-background"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowTestModal(false)}
        />
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--panel-bg)',
            color: 'var(--text-primary)',
            border: '1px solid rgba(68,68,102,0.6)',
            borderRadius: 10,
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            margin: '0 16px',
          }}
        >
          <div
            style={{
              padding: '20px 24px 12px',
              borderBottom: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2
              className="title is-5 mb-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Test Map
            </h2>
            <button
              className="delete is-medium"
              aria-label="close"
              onClick={() => setShowTestModal(false)}
            />
          </div>
          <div style={{ padding: '20px 24px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              In order to playtest you must first save your changes.
            </p>
          </div>
          <div
            style={{
              padding: '12px 24px 20px',
              borderTop: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <button
              className="button is-light"
              onClick={() => setShowTestModal(false)}
            >
              Cancel
            </button>
            <button
              className="button is-primary"
              onClick={handleSaveAndPlay}
              disabled={saving || invalidPlayerCount || !mapState}
            >
              {saving ? 'Saving…' : 'Save and Play'}
            </button>
          </div>
        </div>
      </div>

      <div className={`modal ${showExportModal ? 'is-active' : ''}`}>
        <div
          className="modal-background"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowExportModal(false)}
        />
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--panel-bg)',
            color: 'var(--text-primary)',
            border: '1px solid rgba(68,68,102,0.6)',
            borderRadius: 10,
            maxWidth: 640,
            width: '100%',
            maxHeight: '85vh',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            margin: '0 16px',
          }}
        >
          <div
            style={{
              padding: '20px 24px 12px',
              borderBottom: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2
              className="title is-5 mb-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Export as Code
            </h2>
            <button
              className="delete is-medium"
              aria-label="close"
              onClick={() => setShowExportModal(false)}
            />
          </div>
          <div
            style={{
              padding: '12px 24px 16px',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <textarea
              className="textarea"
              readOnly
              rows={16}
              value={exportText ?? ''}
              style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          </div>
          <div
            style={{
              padding: '12px 24px 20px',
              borderTop: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button className="button is-light" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          </div>
        </div>
      </div>

      <div className={`modal ${showConfigModal ? 'is-active' : ''}`}>
        <div
          className="modal-background"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowConfigModal(false)}
        />
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--panel-bg)',
            color: 'var(--text-primary)',
            border: '1px solid rgba(68,68,102,0.6)',
            borderRadius: 10,
            maxWidth: 500,
            width: '100%',
            maxHeight: '85vh',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            margin: '0 16px',
          }}
        >
          <div
            style={{
              padding: '20px 24px 12px',
              borderBottom: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2
              className="title is-5 mb-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Configuration
            </h2>
            <button
              className="delete is-medium"
              aria-label="close"
              onClick={() => setShowConfigModal(false)}
            />
          </div>
          <div
            style={{
              padding: '12px 24px 16px',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <div className="field">
              <label className="label">Name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setDirty(true);
                }}
              />
            </div>
            <div className="field">
              <label className="label">Description</label>
              <textarea
                className="textarea"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setDirty(true);
                }}
              />
            </div>
            <div className="field">
              <label className="label">Size</label>
              <div className="field has-addons" style={{ flexWrap: 'wrap' }}>
                <div className="control">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={widthInput}
                    onChange={(e) => setWidthInput(Number(e.target.value))}
                    style={{ width: '80px' }}
                  />
                </div>
                <div className="control">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={heightInput}
                    onChange={(e) => setHeightInput(Number(e.target.value))}
                    style={{ width: '80px' }}
                  />
                </div>
                <div className="control">
                  <button className="button" onClick={handleResize}>
                    Resize
                  </button>
                </div>
              </div>
            </div>

            <hr style={{ borderColor: 'rgba(68,68,102,0.35)' }} />

            <label className="label">Player Slots</label>
            {playerSlots.map((slot, index) => {
              const slotColor = PLAYER_COLORS[index % PLAYER_COLORS.length];
              return (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    borderRadius: 10,
                    border: '1px solid var(--panel-border)',
                    background: 'var(--panel-bg-raised)',
                    padding: '14px 16px 14px 20px',
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      borderRadius: '10px 0 0 10px',
                      background: slotColor,
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: slotColor,
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </span>
                    <span
                      style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.85rem',
                      }}
                    >
                      Starting funds
                    </span>
                    <input
                      className="input is-small"
                      type="number"
                      value={slot.startingFunds}
                      onChange={(e) =>
                        updateStartingFunds(index, Number(e.target.value))
                      }
                      style={{ width: 80 }}
                    />
                    {canRemoveSlot(index) && (
                      <button
                        className="button is-small is-light"
                        onClick={() => removePlayerSlot(index)}
                        style={{ marginLeft: 'auto' }}
                      >
                        Del
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {playerSlots.length < 4 && (
              <button
                className="button is-small is-light"
                onClick={addPlayerSlot}
              >
                Add Player Slot
              </button>
            )}
            {invalidPlayerCount && (
              <p className="help is-danger" style={{ marginTop: 8 }}>
                A property is owned by a player slot that no longer exists —
                assign it to an existing slot or add more slots before saving.
              </p>
            )}
          </div>
          <div
            style={{
              padding: '12px 24px 20px',
              borderTop: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              className="button is-light"
              onClick={() => setShowConfigModal(false)}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
